import { useCallback, useEffect, useState } from 'react';

const PROBLEM_SELECTOR = '[role="alert"], [aria-invalid="true"], p[id$="-error"]';

function reveal(root: HTMLElement): boolean {
  const problem = root.querySelector<HTMLElement>(PROBLEM_SELECTOR);
  if (!problem) return false;
  problem.scrollIntoView({ block: 'center' });
  return true;
}

export function useScrollToProblem(): (node: HTMLElement | null) => void {
  const [root, setRoot] = useState<HTMLElement | null>(null);
  const attach = useCallback((node: HTMLElement | null) => setRoot(node), []);

  useEffect(() => {
    if (!root) return undefined;
    let armed = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const disarm = () => {
      armed = false;
      if (timer) clearTimeout(timer);
      timer = null;
    };
    let settle: ReturnType<typeof setTimeout> | null = null;
    const observer = new MutationObserver(() => {
      if (!armed) return;
      if (settle) clearTimeout(settle);
      settle = setTimeout(() => {
        if (armed && reveal(root)) disarm();
      }, 120);
    });
    const onSubmit = () => {
      armed = true;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        if (armed && reveal(root)) disarm();
        else timer = setTimeout(disarm, 8000);
      }, 80);
    };
    root.addEventListener('submit', onSubmit, true);
    observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-invalid'] });
    return () => {
      root.removeEventListener('submit', onSubmit, true);
      observer.disconnect();
      if (settle) clearTimeout(settle);
      disarm();
    };
  }, [root]);

  return attach;
}
